'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Mail, MessageSquare, Phone, PhoneCall, Star, Voicemail } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen } from './demoHooks'
import { ResultStamp } from './demoParts'

// A follow-up sequence on a day slider: each automated touch fires on its
// day, the customer answers at some point, and every later touch is skipped
// because GoHighLevel already knows they booked. The slider plays on its own
// and the visitor can drag it. Used by the dental and home-services pages.

const CHANNEL_ICON = { email: Mail, sms: MessageSquare, voice: Voicemail, call: PhoneCall, phone: Phone, review: Star }

export default function FollowUpScrubber({ title, label, scenarios }) {
  const [ref, onScreen] = useOnScreen()
  const reduce = useReducedMotion()
  const keys = Object.keys(scenarios)
  const [key, setKey] = useState(keys[0])
  const [run, setRun] = useState(0)
  const s = scenarios[key]
  const [day, setDay] = useState(reduce ? s.maxDay : s.startDay)
  const [playing, setPlaying] = useState(true)

  const [seen, setSeen] = useState(`${key}-${run}`)
  if (seen !== `${key}-${run}`) {
    setSeen(`${key}-${run}`)
    setDay(reduce ? s.maxDay : s.startDay)
    setPlaying(true)
  }

  // Plays forward a day at a time, pausing a beat on days where something happens.
  useEffect(() => {
    if (!onScreen || !playing || reduce || day >= s.maxDay) return undefined
    const eventful = s.touches.some((t) => t.day === day) || s.reply?.day === day
    const timer = setTimeout(() => setDay((d) => d + 1), eventful ? 1300 : 160)
    return () => clearTimeout(timer)
  }, [onScreen, playing, reduce, day, s])

  const replied = s.reply && day >= s.reply.day
  const touches = s.touches.map((t) => ({
    ...t,
    state: replied && t.day > s.reply.day ? 'skipped' : day >= t.day ? 'sent' : 'next',
  }))
  const latest = [...touches].reverse().find((t) => t.state === 'sent')
  const pick = (next) => { setKey(next); setRun((n) => n + 1) }
  const span = s.maxDay - s.startDay

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-scrub"
      title={title}
      label={label}
      tabs={keys.length > 1 ? keys.map((k) => ({ key: k, label: scenarios[k].tab })) : undefined}
      activeTab={key}
      onTab={pick}
      onReplay={() => pick(key)}
    >
      <div className="svd-person">
        <span className="svd-person-avatar" aria-hidden="true">{s.person.initials}</span>
        <span><strong>{s.person.name}</strong><small>{s.person.detail}</small></span>
        <span className={`svd-chip${replied ? ' is-good' : ' is-wait'}`}>{replied ? s.reply.status : s.person.status}</span>
      </div>

      <div className="svd-scrub-track">
        <div className="svd-scrub-rail" aria-hidden="true">
          <i style={{ width: `${((day - s.startDay) / span) * 100}%` }} />
          {touches.map((t) => {
            const Icon = CHANNEL_ICON[t.channel]
            return (
              <span key={t.day} className={`svd-scrub-dot is-${t.state}`} style={{ left: `${((t.day - s.startDay) / span) * 100}%` }}>
                <Icon size={12} />
              </span>
            )
          })}
        </div>
        <input
          type="range"
          min={s.startDay}
          max={s.maxDay}
          value={day}
          onChange={(e) => { setPlaying(false); setDay(Number(e.target.value)) }}
          className="svd-scrub-input"
          aria-label={`Drag to move through the sequence, ${s.dayLabel(day)}`}
        />
        <div className="svd-scrub-labels" aria-hidden="true">
          <span className={day === s.startDay ? 'is-hidden' : ''}>{s.dayLabel(s.startDay)}</span>
          <b>{s.dayLabel(day)}</b>
          <span className={day === s.maxDay ? 'is-hidden' : ''}>{s.dayLabel(s.maxDay)}</span>
        </div>
      </div>

      <ul className="svd-touches">
        {touches.map((t) => {
          const Icon = CHANNEL_ICON[t.channel]
          return (
            <li key={t.day} className={`is-${t.state}`}>
              <span className="svd-touch-icon" aria-hidden="true"><Icon size={14} /></span>
              <span className="svd-touch-when">{s.dayLabel(t.day)}</span>
              <span className="svd-touch-what">{t.label}</span>
              <span className="svd-touch-state">{t.state === 'sent' ? 'Sent' : t.state === 'skipped' ? 'Not needed' : 'Scheduled'}</span>
            </li>
          )
        })}
      </ul>

      <div className="svd-scrub-message" aria-live="polite">
        <AnimatePresence mode="wait">
          {replied ? (
            <motion.p key="reply" className="svd-bubble is-lead" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <small className="svd-bubble-meta">{s.person.name.split(' ')[0]} replied</small>{s.reply.text}
            </motion.p>
          ) : latest ? (
            <motion.p key={latest.day} className="svd-bubble is-bot" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <small className="svd-bubble-meta">{latest.label}</small>{latest.message}
            </motion.p>
          ) : (
            <motion.p key="wait" className="svd-scrub-idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{s.idle}</motion.p>
          )}
        </AnimatePresence>
      </div>

      <ResultStamp show={replied} tone="good" text={s.reply?.stamp} id={`${key}-${run}`} />
    </DemoFrame>
  )
}
